import { motion } from "framer-motion";

/**
 * Signature korv. matte pump bottle — for Ritual Cleanser, Body Oil, etc.
 * Tall, weighted matte glass with matte-black pump head.
 */
export default function KorvBottle({
  color = "#e6ddcf",
  accent = "#141212",
  label = "korv.",
  productLabel = "RITUAL CLEANSER",
  notes = ["Smoked Frankincense", "Honey", "Labdanum"],
  volume = "300 ml · 10.1 fl oz",
  className = "",
  animate = true,
}) {
  const uid = (color + accent).replace(/[^a-z0-9]/gi, "");
  const dark = isDark(color);
  const inkText   = dark ? "#EDE6D8" : "#141212";
  const softText  = dark ? "rgba(237,230,216,0.65)" : "rgba(20,18,17,0.55)";
  const microText = dark ? "rgba(237,230,216,0.42)" : "rgba(20,18,17,0.42)";

  return (
    <motion.svg
      viewBox="0 0 220 620"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      initial={animate ? { y: 30, opacity: 0 } : false}
      whileInView={animate ? { y: 0, opacity: 1 } : false}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 1.6, ease: [0.2, 0.7, 0.1, 1] }}
      aria-hidden="true"
    >
      <defs>
        <radialGradient id={`gr-${uid}`} cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor="rgba(20,18,17,0.42)" />
          <stop offset="0.55" stopColor="rgba(20,18,17,0.16)" />
          <stop offset="1" stopColor="rgba(20,18,17,0)" />
        </radialGradient>
        <filter id={`sblur-${uid}`}><feGaussianBlur stdDeviation="5" /></filter>

        {/* Matte body shading */}
        <linearGradient id={`body-${uid}`} x1="0.05" y1="0" x2="0.98" y2="0">
          <stop offset="0"    stopColor="rgba(255,255,255,0.00)" />
          <stop offset="0.05" stopColor="rgba(255,255,255,0.20)" />
          <stop offset="0.22" stopColor="rgba(255,255,255,0.08)" />
          <stop offset="0.55" stopColor="rgba(0,0,0,0)" />
          <stop offset="0.82" stopColor="rgba(0,0,0,0.14)" />
          <stop offset="1"    stopColor="rgba(0,0,0,0.32)" />
        </linearGradient>
        <linearGradient id={`vert-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="rgba(0,0,0,0.24)" />
          <stop offset="0.14" stopColor="rgba(0,0,0,0.02)" />
          <stop offset="0.6" stopColor="rgba(0,0,0,0)" />
          <stop offset="0.9" stopColor="rgba(0,0,0,0.08)" />
          <stop offset="1" stopColor="rgba(0,0,0,0.26)" />
        </linearGradient>

        {/* Pump */}
        <linearGradient id={`pump-${uid}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="rgba(255,255,255,0.20)" />
          <stop offset="0.5" stopColor="rgba(255,255,255,0)" />
          <stop offset="1" stopColor="rgba(0,0,0,0.35)" />
        </linearGradient>

        <clipPath id={`bc-${uid}`}>
          <rect x="40" y="150" width="140" height="410" rx="6" />
        </clipPath>
      </defs>

      {/* Ground shadow */}
      <ellipse cx="110" cy="590" rx="80" ry="8" fill={`url(#gr-${uid})`} filter={`url(#sblur-${uid})`} />
      <ellipse cx="110" cy="586" rx="55" ry="4" fill="rgba(20,18,17,0.28)" />

      {/* Pump nozzle */}
      <g>
        {/* dispenser head — the extended nozzle */}
        <rect x="70" y="20" width="30" height="30" fill={accent} />
        <rect x="70" y="20" width="30" height="30" fill={`url(#pump-${uid})`} />
        {/* top of pump */}
        <rect x="65" y="50" width="80" height="16" rx="2" fill={accent} />
        {/* neck stem to collar */}
        <rect x="85" y="66" width="40" height="14" fill={accent} />
        {/* collar cap */}
        <rect x="60" y="80" width="100" height="46" rx="3" fill={accent} />
        <rect x="60" y="80" width="100" height="46" rx="3" fill={`url(#pump-${uid})`} />
        <rect x="60" y="123" width="100" height="3" fill="rgba(0,0,0,0.3)" />
      </g>

      {/* Body shoulder curve */}
      <path d="M 60 126 L 60 150 L 40 150 L 40 560 Q 40 585 60 585 L 160 585 Q 180 585 180 560 L 180 150 L 160 150 L 160 126 Z"
        fill={color} />
      <g clipPath={`url(#bc-${uid})`}>
        <rect x="0" y="126" width="220" height="470" fill={`url(#body-${uid})`} />
        <rect x="0" y="126" width="220" height="470" fill={`url(#vert-${uid})`} />
      </g>
      <path d="M 60 126 L 60 150 L 40 150 L 40 560 Q 40 585 60 585 L 160 585 Q 180 585 180 560 L 180 150 L 160 150 L 160 126"
        fill="none" stroke="rgba(0,0,0,0.20)" strokeWidth="0.6" />

      {/* korv. wordmark */}
      <text x="110" y="260" textAnchor="middle" fill={inkText}
        fontFamily="Fraunces, serif" fontWeight="500" fontSize="32" letterSpacing="-1"
        style={{ fontVariationSettings: "'opsz' 144" }}>
        {label}
      </text>

      {/* Product label */}
      <line x1="80" y1="310" x2="140" y2="310" stroke={softText} strokeWidth="0.6" />
      <text x="110" y="336" textAnchor="middle" fill={softText}
        fontFamily="'Inter Tight', sans-serif" fontWeight="500" fontSize="10" letterSpacing="3">
        {productLabel}
      </text>

      {/* Fragrance notes */}
      {notes.slice(0, 3).map((n, i) => (
        <text key={i} x="110" y={370 + i * 14} textAnchor="middle" fill={microText}
          fontFamily="'Inter Tight', sans-serif" fontWeight="300" fontSize="7.5" letterSpacing="1.4">
          {n}
        </text>
      ))}

      {/* Volume at bottom */}
      <text x="110" y="540" textAnchor="middle" fill={microText}
        fontFamily="'Inter Tight', sans-serif" fontWeight="300" fontSize="6.5" letterSpacing="1.4">
        {volume}
      </text>
    </motion.svg>
  );
}

function isDark(hex) {
  const c = hex.replace("#", "");
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return (r * 0.299 + g * 0.587 + b * 0.114) < 140;
}
